import React, { useState } from "react";
import { useParams } from "react-router";
import BeatLoader from "react-spinners/BeatLoader";
import { Constants } from "../../../../../common/constants";
import {
	WebhookEventTypeGroups,
	WebhookEventType,
} from "../../../../../common/api_types";
import { RouterInferenceHelpers } from "../../../../../src/api/trpc/rootRouter";
import { trpc } from "../../utils/trpc";
import { DashboardUtils } from "../../dashboard_utils";
import { ErrorNote } from "../../forms/improved_components/error_note";
import { SeshPrimaryButton } from "../../forms/improved_components/base_components/sesh_primary_button";

interface ApiSettingsProps {
	is_admin: boolean;
}

// Use inferred type from tRPC but with proper event_types typing for local state
type WebhookFromApi =
	RouterInferenceHelpers["guild_api"]["listWebhooks"]["output"][number];

interface EditableWebhook {
	id: number;
	url: string;
	event_types: WebhookEventType[];
	is_enabled: boolean;
	created_at: Date;
	description: string | null;
	failure_count: number;
}

interface NewWebhook {
	url: string;
	event_types: WebhookEventType[];
	description: string;
}

// Helper to convert API webhook to editable webhook with proper typing
function toEditableWebhook(webhook: WebhookFromApi): EditableWebhook {
	return {
		...webhook,
		event_types: webhook.event_types as WebhookEventType[],
	};
}

export function ApiSettings({ is_admin }: ApiSettingsProps) {
	const { server_id } = useParams<{ server_id: string }>();

	// State
	const [generatedToken, setGeneratedToken] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [showCreateWebhook, setShowCreateWebhook] = useState(false);
	const [editingWebhook, setEditingWebhook] =
		useState<EditableWebhook | null>(null);
	const [newWebhook, setNewWebhook] = useState<NewWebhook>({
		url: "",
		event_types: [],
		description: "",
	});
	const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(
		null
	);
	const [togglingWebhookId, setTogglingWebhookId] = useState<number | null>(
		null
	);

	// tRPC queries
	const apiAccessQuery = trpc.guild_api.getApiAccess.useQuery(
		{ guild_id: server_id! },
		{ enabled: !!server_id }
	);

	const webhooksQuery = trpc.guild_api.listWebhooks.useQuery(
		{ guild_id: server_id! },
		{ enabled: !!server_id && apiAccessQuery.data?.has_custom_tier }
	);

	const trpcContext = trpc.useContext();

	const generateTokenMutation = trpc.guild_api.generateToken.useMutation({
		onSuccess: (data) => {
			setGeneratedToken(data.token);
			trpcContext.guild_api.getApiAccess.invalidate({ guild_id: server_id });
		},
	});

	const createWebhookMutation = trpc.guild_api.createWebhook.useMutation({
		onSuccess: (data) => {
			setNewWebhookSecret(data.secret);
			setShowCreateWebhook(false);
			setNewWebhook({ url: "", event_types: [], description: "" });
			trpcContext.guild_api.listWebhooks.invalidate({ guild_id: server_id });
		},
	});

	const toggleWebhookMutation = trpc.guild_api.updateWebhook.useMutation({
		onSuccess: () => {
			setTogglingWebhookId(null);
			trpcContext.guild_api.listWebhooks.invalidate({ guild_id: server_id });
		},
		onError: () => {
			setTogglingWebhookId(null);
		},
	});

	const updateWebhookMutation = trpc.guild_api.updateWebhook.useMutation({
		onSuccess: () => {
			setEditingWebhook(null);
			trpcContext.guild_api.listWebhooks.invalidate({ guild_id: server_id });
		},
	});

	const deleteWebhookMutation = trpc.guild_api.deleteWebhook.useMutation({
		onSuccess: () => {
			trpcContext.guild_api.listWebhooks.invalidate({ guild_id: server_id });
		},
	});

	// Handlers
	const handleCopyToken = async () => {
		if (generatedToken) {
			await navigator.clipboard.writeText(generatedToken);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const handleCopySecret = async (secret: string) => {
		await navigator.clipboard.writeText(secret);
		DashboardUtils.ShowSuccessMessageAlert(
			"Copied!",
			"The webhook secret has been copied to your clipboard."
		);
	};

	const handleGenerateToken = async () => {
		const hasExistingToken = apiAccessQuery.data?.has_token;
		if (hasExistingToken) {
			const result = await DashboardUtils.ShowDeleteConfirmAlert(
				<>
					<p>
						Regenerating your API token will immediately invalidate
						your existing token. Any applications using the old
						token will stop working.
					</p>
					<p className="mt-4 has-text-weight-bold">
						Are you sure you want to regenerate the token?
					</p>
				</>
			);
			if (!result.isConfirmed) return;
		}
		generateTokenMutation.mutate({ guild_id: server_id! });
	};

	const handleCreateWebhook = () => {
		if (newWebhook.url && newWebhook.event_types.length > 0) {
			createWebhookMutation.mutate({
				guild_id: server_id!,
				url: newWebhook.url,
				event_types: newWebhook.event_types,
				description: newWebhook.description || null,
			});
		}
	};

	const handleDeleteWebhook = async (webhook: WebhookFromApi) => {
		const result = await DashboardUtils.ShowDeleteConfirmAlert(
			<>
				<p>Are you sure you want to delete this webhook?</p>
				<p className="mt-2">
					<code style={{ color: "#00c3cc" }}>{webhook.url}</code>
				</p>
			</>
		);
		if (result.isConfirmed) {
			deleteWebhookMutation.mutate({
				guild_id: server_id!,
				webhook_id: webhook.id,
			});
		}
	};

	const handleToggleWebhook = (webhook: WebhookFromApi) => {
		setTogglingWebhookId(webhook.id);
		toggleWebhookMutation.mutate({
			guild_id: server_id!,
			webhook_id: webhook.id,
			is_enabled: !webhook.is_enabled,
		});
	};

	const handleUpdateWebhook = () => {
		if (editingWebhook) {
			updateWebhookMutation.mutate({
				guild_id: server_id!,
				webhook_id: editingWebhook.id,
				url: editingWebhook.url,
				event_types: editingWebhook.event_types,
				description: editingWebhook.description,
			});
		}
	};

	const toggleEventType = (
		eventType: WebhookEventType,
		current: WebhookEventType[],
		setter: (types: WebhookEventType[]) => void
	) => {
		if (current.includes(eventType)) {
			setter(current.filter((t) => t !== eventType));
		} else {
			setter([...current, eventType]);
		}
	};

	const toggleEventGroup = (
		groupEvents: readonly WebhookEventType[],
		current: WebhookEventType[],
		setter: (types: WebhookEventType[]) => void
	) => {
		const allSelected = groupEvents.every((e) => current.includes(e));
		if (allSelected) {
			setter(current.filter((t) => !groupEvents.includes(t)));
		} else {
			const newTypes = [...current];
			groupEvents.forEach((e) => {
				if (!newTypes.includes(e)) newTypes.push(e);
			});
			setter(newTypes);
		}
	};

	// Loading state
	if (apiAccessQuery.isLoading) {
		return (
			<div className="has-text-centered py-6">
				<BeatLoader color={Constants.EMBED_COLOR} />
			</div>
		);
	}

	// Upgrade prompt for non-custom-tier guilds
	if (!apiAccessQuery.data?.has_custom_tier) {
		return (
			<div className="has-text-centered py-6">
				<p className="mb-4">
					Access the <span className="sesh-text">sesh</span> REST API
					to programmatically manage events, polls, and more. Set up
					webhooks to receive real-time notifications when things
					happen in your server.
				</p>
				<div
					className="rounded-xl p-6"
					style={{
						maxWidth: "400px",
						margin: "0 auto",
						background: "linear-gradient(to bottom right, rgba(8, 145, 178, 0.2), rgba(30, 41, 59, 0.8))",
						border: "1px solid rgba(0, 244, 255, 0.3)",
					}}
				>
					<p className="mb-4 text-content-primary">
						<i className="fas fa-crown mr-2 text-sesh-primary"></i>
						This feature requires{" "}
						<strong className="text-sesh-primary">sesh Pro</strong>.
					</p>
					<a
						href="/dashboard/premium"
						className="inline-flex items-center gap-2 rounded-lg bg-sesh-primary px-4 py-2 font-semibold text-base-dark transition-all hover:bg-cyan-300"
					>
						<i className="fas fa-arrow-right"></i>
						View Premium Options
					</a>
				</div>
			</div>
		);
	}

	// Permission check
	if (!is_admin) {
		return (
			<ErrorNote error="You need Manage Server permissions to configure API settings." />
		);
	}

	return (
		<div className="api-settings">
			{/* Security Warning */}
			<div
				className="mb-5 rounded-lg p-4"
				style={{
					backgroundColor: "rgba(251, 191, 36, 0.1)",
					border: "1px solid rgba(251, 191, 36, 0.3)",
				}}
			>
				<p className="text-amber-200">
					<i className="fas fa-exclamation-triangle mr-2 text-amber-400"></i>
					<strong>Security Notice:</strong> Your API token grants full
					access to manage events, polls, and other resources in this
					server. Keep it secret and never share it publicly.
				</p>
			</div>

			{/* API Token Section */}
			<div className="mb-6">
				<h3 className="title is-5 mb-3">
					<i className="fas fa-key mr-2"></i>
					API Token
				</h3>

				{apiAccessQuery.data?.has_token ? (
					<div className="mb-4">
						<p className="mb-2">
							Token prefix:{" "}
							<code className="rounded-sm bg-gray-800 px-2 py-0.5 text-white">{apiAccessQuery.data.token_prefix}...</code>
						</p>
						{apiAccessQuery.data.token_created_at && (
							<p className="is-size-7 has-text-grey">
								Created:{" "}
								{new Date(
									apiAccessQuery.data.token_created_at
								).toLocaleDateString()}
							</p>
						)}
						{apiAccessQuery.data.token_last_used_at && (
							<p className="is-size-7 has-text-grey">
								Last used:{" "}
								{new Date(
									apiAccessQuery.data.token_last_used_at
								).toLocaleDateString()}
							</p>
						)}
					</div>
				) : (
					<p className="mb-4">
						No API token has been generated yet. Generate one to
						start using the API.
					</p>
				)}

				{generatedToken && (
					<div
						className="mb-4 rounded-lg p-4"
						style={{
							backgroundColor: "rgba(34, 197, 94, 0.1)",
							border: "1px solid rgba(34, 197, 94, 0.3)",
						}}
					>
						<p className="mb-2 text-green-300">
							<strong>
								<i className="fas fa-check-circle mr-2 text-green-400"></i>
								Token Generated!
							</strong>
						</p>
						<p className="mb-2 text-green-200">
							Copy this token now - it will not be shown again.
						</p>
						<div className="is-flex is-align-items-center">
							<code
								className="mr-3 text-green-100"
								style={{
									wordBreak: "break-all",
									padding: "0.5rem",
									backgroundColor: "rgba(0,0,0,0.3)",
									borderRadius: "4px",
								}}
							>
								{generatedToken}
							</code>
							<button
								className="button is-small"
								onClick={handleCopyToken}
							>
								<i
									className={`fas ${copied ? "fa-check" : "fa-copy"} mr-1`}
								></i>
								{copied ? "Copied!" : "Copy"}
							</button>
						</div>
					</div>
				)}

				<SeshPrimaryButton
					onClick={handleGenerateToken}
					disabled={generateTokenMutation.isLoading}
					is_loading={generateTokenMutation.isLoading}
					className="px-4"
				>
					<i
						className={`fas ${apiAccessQuery.data?.has_token ? "fa-sync" : "fa-plus"} mr-2`}
					></i>
					{apiAccessQuery.data?.has_token
						? "Regenerate Token"
						: "Generate Token"}
				</SeshPrimaryButton>
			</div>

			{/* Quick Start Guide */}
			<div className="mb-6">
				<h3 className="title is-5 mb-3">
					<i className="fas fa-rocket mr-2"></i>
					Quick Start
				</h3>
				<p className="mb-3">
					Use your token to authenticate API requests. Here is an
					example using curl:
				</p>
				<pre
					style={{
						backgroundColor: "#29303a",
						padding: "1rem",
						borderRadius: "6px",
						overflow: "auto",
						border: "1px solid #3a424d",
					}}
				>
					<code style={{ color: "#c8cfd8" }}>
						<span style={{ color: "#cc99cd" }}>curl</span>{" "}
						<span style={{ color: "#f8c555" }}>-X</span> GET{" "}
						<span style={{ color: "#7ec699" }}>"https://sesh.fyi/api/v1/events"</span>{" "}
						<span style={{ color: "#cc99cd" }}>\</span>{"\n"}
						{"  "}<span style={{ color: "#f8c555" }}>-H</span>{" "}
						<span style={{ color: "#7ec699" }}>"Authorization: Bearer YOUR_API_TOKEN"</span>{" "}
						<span style={{ color: "#cc99cd" }}>\</span>{"\n"}
						{"  "}<span style={{ color: "#f8c555" }}>-H</span>{" "}
						<span style={{ color: "#7ec699" }}>"Content-Type: application/json"</span>
					</code>
				</pre>
				<p className="mt-3">
					<a
						href="/manual/api/"
						target="_blank"
						rel="noreferrer"
						className="manual-link"
					>
						<i className="fas fa-book mr-1"></i>
						View Full API Documentation
					</a>
				</p>
			</div>

			{/* Webhooks Section */}
			<div className="mb-6">
				<div className="is-flex is-align-items-center is-justify-content-space-between mb-3">
					<h3 className="title is-5 mb-0">
						<i className="fas fa-broadcast-tower mr-2"></i>
						Webhooks
					</h3>
					<button
						className="button is-small is-primary"
						onClick={() => setShowCreateWebhook(true)}
						disabled={
							(webhooksQuery.data?.length ?? 0) >= 10 ||
							showCreateWebhook
						}
					>
						<i className="fas fa-plus mr-1"></i>
						Add Webhook
					</button>
				</div>

				<p className="mb-4">
					Webhooks let you receive real-time HTTP POST notifications
					when events occur in your server.
				</p>

				{/* New Webhook Secret Display */}
				{newWebhookSecret && (
					<div
						className="relative mb-4 rounded-lg p-4"
						style={{
							backgroundColor: "rgba(34, 197, 94, 0.1)",
							border: "1px solid rgba(34, 197, 94, 0.3)",
						}}
					>
						<button
							className="absolute right-2 top-2 h-5 w-5 rounded-full text-green-300 hover:bg-green-900/50"
							onClick={() => setNewWebhookSecret(null)}
						>
							<i className="fas fa-times text-xs"></i>
						</button>
						<p className="mb-2 text-green-300">
							<strong>
								<i className="fas fa-check-circle mr-2 text-green-400"></i>
								Webhook Created!
							</strong>
						</p>
						<p className="mb-2 text-green-200">
							Copy this secret now - it will not be shown again.
							Use it to verify webhook signatures.
						</p>
						<div className="is-flex is-align-items-center">
							<code
								className="mr-3 text-green-100"
								style={{
									wordBreak: "break-all",
									padding: "0.5rem",
									backgroundColor: "rgba(0,0,0,0.3)",
									borderRadius: "4px",
								}}
							>
								{newWebhookSecret}
							</code>
							<button
								className="button is-small"
								onClick={() =>
									handleCopySecret(newWebhookSecret)
								}
							>
								<i className="fas fa-copy mr-1"></i>
								Copy
							</button>
						</div>
					</div>
				)}

				{/* Create Webhook Form */}
				{showCreateWebhook && (
					<div className="mb-4 rounded-lg bg-base-dark p-4">
						<h4 className="title is-6 mb-3">New Webhook</h4>

						<div className="field">
							<label className="label">URL</label>
							<div className="control">
								<input
									type="url"
									className="input"
									placeholder="https://your-server.com/webhook"
									value={newWebhook.url}
									onChange={(e) =>
										setNewWebhook({
											...newWebhook,
											url: e.target.value,
										})
									}
								/>
							</div>
							<p className="help">Must use HTTPS</p>
						</div>

						<div className="field">
							<label className="label">
								Description (optional)
							</label>
							<div className="control">
								<input
									type="text"
									className="input"
									placeholder="My webhook"
									maxLength={128}
									value={newWebhook.description}
									onChange={(e) =>
										setNewWebhook({
											...newWebhook,
											description: e.target.value,
										})
									}
								/>
							</div>
						</div>

						<div className="field">
							<label className="label">Events</label>
							<EventTypeSelector
								selectedTypes={newWebhook.event_types}
								onToggleType={(type) =>
									toggleEventType(
										type,
										newWebhook.event_types,
										(types) =>
											setNewWebhook({
												...newWebhook,
												event_types: types,
											})
									)
								}
								onToggleGroup={(groupEvents) =>
									toggleEventGroup(
										groupEvents,
										newWebhook.event_types,
										(types) =>
											setNewWebhook({
												...newWebhook,
												event_types: types,
											})
									)
								}
							/>
						</div>

						<div className="is-flex is-justify-content-flex-end mt-4">
							<button
								className="mr-2 rounded-md px-4 py-2 text-slate-300 transition-colors hover:bg-base-light hover:text-white"
								onClick={() => {
									setShowCreateWebhook(false);
									setNewWebhook({
										url: "",
										event_types: [],
										description: "",
									});
								}}
							>
								Cancel
							</button>
							<SeshPrimaryButton
								onClick={handleCreateWebhook}
								disabled={
									!newWebhook.url ||
									newWebhook.event_types.length === 0 ||
									createWebhookMutation.isLoading
								}
								is_loading={createWebhookMutation.isLoading}
								className="px-4"
							>
								Create Webhook
							</SeshPrimaryButton>
						</div>
					</div>
				)}

				{/* Webhooks List */}
				{webhooksQuery.isLoading ? (
					<div className="has-text-centered py-4">
						<BeatLoader color={Constants.EMBED_COLOR} size={10} />
					</div>
				) : webhooksQuery.data?.length === 0 ? (
					<p className="has-text-grey">
						No webhooks configured yet. Click "Add Webhook" to
						create one.
					</p>
				) : (
					<div className="webhooks-list">
						{webhooksQuery.data?.map((webhook) => (
							<div
								key={webhook.id}
								className="box mb-3"
								style={{
									backgroundColor: "#2a2a2a",
									opacity: webhook.is_enabled ? 1 : 0.7,
								}}
							>
								<div className="is-flex is-align-items-center is-justify-content-space-between mb-2">
									<div className="is-flex is-align-items-center is-flex-wrap-wrap" style={{ gap: "0.5rem" }}>
										<span
											className={`tag ${webhook.is_enabled ? "is-success" : "is-light"}`}
										>
											{webhook.is_enabled
												? "Enabled"
												: "Disabled"}
										</span>
										{webhook.failure_count > 0 && (
											<span
												className={`tag ${webhook.failure_count >= 5 ? "is-danger" : webhook.failure_count >= 3 ? "is-warning" : "is-light"}`}
												title="Delivery failures (auto-disables at 5)"
											>
												<i className="fas fa-exclamation-triangle mr-1"></i>
												{webhook.failure_count}/5 failures
											</span>
										)}
										{webhook.description && (
											<strong>
												{webhook.description}
											</strong>
										)}
									</div>
									<div className="is-flex">
										<button
											className="button is-small mr-1"
											onClick={() =>
												handleToggleWebhook(webhook)
											}
											disabled={
												togglingWebhookId === webhook.id
											}
											title={
												webhook.is_enabled
													? "Disable"
													: "Enable"
											}
										>
											<i
												className={`fas ${webhook.is_enabled ? "fa-pause" : "fa-play"}`}
											></i>
										</button>
										<button
											className="button is-small mr-1"
											onClick={() =>
												setEditingWebhook(
													toEditableWebhook(webhook)
												)
											}
											title="Edit"
										>
											<i className="fas fa-edit"></i>
										</button>
										<button
											className="button is-small is-danger"
											onClick={() =>
												handleDeleteWebhook(webhook)
											}
											disabled={
												deleteWebhookMutation.isLoading
											}
											title="Delete"
										>
											<i className="fas fa-trash"></i>
										</button>
									</div>
								</div>
								<p
									className="is-size-7 mb-2"
									style={{ wordBreak: "break-all" }}
								>
									<code style={{ color: "#00c3cc" }}>{webhook.url}</code>
								</p>
								{!webhook.is_enabled && webhook.failure_count >= 5 && (
									<p
										className="is-size-7 mb-2"
										style={{ color: "#f14668" }}
									>
										<i className="fas fa-exclamation-circle mr-1"></i>
										Auto-disabled due to repeated delivery failures. Re-enable to reset the counter and resume deliveries.
									</p>
								)}
								<div className="tags">
									{webhook.event_types.map((type) => (
										<span
											key={type}
											className="tag is-dark is-small"
										>
											{type}
										</span>
									))}
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Edit Webhook Modal */}
			{editingWebhook && (
				<DashboardUtils.SeshModal
					is_active={true}
					onClose={() => setEditingWebhook(null)}
					title="Edit Webhook"
					content={
						<div className="mt-4">
							<div className="field">
								<label className="label">URL</label>
								<div className="control">
									<input
										type="url"
										className="input"
										value={editingWebhook.url}
										onChange={(e) =>
											setEditingWebhook({
												...editingWebhook,
												url: e.target.value,
											})
										}
									/>
								</div>
								<p className="help">Must use HTTPS</p>
							</div>

							<div className="field">
								<label className="label">
									Description (optional)
								</label>
								<div className="control">
									<input
										type="text"
										className="input"
										maxLength={128}
										value={editingWebhook.description || ""}
										onChange={(e) =>
											setEditingWebhook({
												...editingWebhook,
												description:
													e.target.value || null,
											})
										}
									/>
								</div>
							</div>

							<div className="field">
								<label className="label">Events</label>
								<EventTypeSelector
									selectedTypes={editingWebhook.event_types}
									onToggleType={(type) =>
										toggleEventType(
											type,
											editingWebhook.event_types,
											(types) =>
												setEditingWebhook({
													...editingWebhook,
													event_types: types,
												})
										)
									}
									onToggleGroup={(groupEvents) =>
										toggleEventGroup(
											groupEvents,
											editingWebhook.event_types,
											(types) =>
												setEditingWebhook({
													...editingWebhook,
													event_types: types,
												})
										)
									}
								/>
							</div>

							<div className="is-flex is-justify-content-flex-end mt-4">
								<button
									className="button is-light mr-2"
									onClick={() => setEditingWebhook(null)}
								>
									Cancel
								</button>
								<SeshPrimaryButton
									onClick={handleUpdateWebhook}
									disabled={
										!editingWebhook.url ||
										editingWebhook.event_types.length ===
											0 ||
										updateWebhookMutation.isLoading
									}
									is_loading={updateWebhookMutation.isLoading}
									className="px-4"
								>
									Save Changes
								</SeshPrimaryButton>
							</div>
						</div>
					}
				/>
			)}
		</div>
	);
}

// Event Type Selector Component
function EventTypeSelector({
	selectedTypes,
	onToggleType,
	onToggleGroup,
}: {
	selectedTypes: WebhookEventType[];
	onToggleType: (type: WebhookEventType) => void;
	onToggleGroup: (groupEvents: readonly WebhookEventType[]) => void;
}) {
	return (
		<div className="event-type-selector">
			{Object.entries(WebhookEventTypeGroups).map(
				([groupName, events]) => {
					const allSelected = events.every((e) =>
						selectedTypes.includes(e)
					);
					const someSelected = events.some((e) =>
						selectedTypes.includes(e)
					);

					return (
						<div key={groupName} className="mb-3">
							<div className="is-flex is-align-items-center mb-2">
								<label className="checkbox">
									<input
										type="checkbox"
										checked={allSelected}
										ref={(el) => {
											if (el) {
												el.indeterminate =
													someSelected && !allSelected;
											}
										}}
										onChange={() => onToggleGroup(events)}
										className="mr-2"
									/>
									<strong>{groupName}</strong>
								</label>
							</div>
							<div className="ml-4">
								{events.map((eventType) => (
									<label
										key={eventType}
										className="checkbox is-block mb-1"
									>
										<input
											type="checkbox"
											checked={selectedTypes.includes(
												eventType
											)}
											onChange={() =>
												onToggleType(eventType)
											}
											className="mr-2"
										/>
										<span className="is-size-7">
											{eventType}
										</span>
									</label>
								))}
							</div>
						</div>
					);
				}
			)}
		</div>
	);
}
